import { motion } from 'framer-motion';
import {
  SiAdobephotoshop,
  SiAdobeillustrator,
  SiAdobepremierepro,
  SiAdobeaftereffects,
  SiFigma,
} from 'react-icons/si';
import { FaPaintBrush, FaCamera, FaPenNib, FaVideo, FaBullhorn, FaPalette, FaLightbulb, FaUsers } from 'react-icons/fa';
import { SkillBadge } from '../components/SkillBadge';
import { SectionTitle } from '../components/SectionTitle';

interface Skill {
  name: string;
  icon: any;
  proficiency?: number;
}

interface SkillCategory {
  title: string;
  skills: Skill[];
}

const skillCategories: SkillCategory[] = [
  {
    title: 'Creative Direction',
    skills: [
      { name: 'Art Direction', icon: FaPaintBrush, proficiency: 95 },
      { name: 'Branding', icon: FaLightbulb, proficiency: 92 },
      { name: 'Strategy', icon: FaBullhorn, proficiency: 90 },
      { name: 'Graphic Design', icon: FaPalette, proficiency: 93 },
    ],
  },
  {
    title: 'Content & Media',
    skills: [
      { name: 'Photography', icon: FaCamera, proficiency: 88 },
      { name: 'Copywriting', icon: FaPenNib, proficiency: 85 },
      { name: 'Video Editing', icon: FaVideo, proficiency: 82 },
      { name: 'Social Media', icon: FaUsers, proficiency: 90 },
    ],
  },
  {
    title: 'Design Tools',
    skills: [
      { name: 'Photoshop', icon: SiAdobephotoshop, proficiency: 92 },
      { name: 'Illustrator', icon: SiAdobeillustrator, proficiency: 90 },
      { name: 'Premiere Pro', icon: SiAdobepremierepro, proficiency: 85 },
      { name: 'After Effects', icon: SiAdobeaftereffects, proficiency: 80 },
      { name: 'Figma', icon: SiFigma, proficiency: 88 },
    ],
  },
];

export const Skills = () => {
  return (
    <section id="skills" className="py-20 relative overflow-hidden">
      {/* Background gradient effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 -left-40 w-96 h-96 bg-primary-500/10 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 -right-40 w-96 h-96 bg-secondary-500/10 rounded-full blur-3xl" />
      </div>

      <div className="section-container relative z-10">
        <SectionTitle
          title="Skills & Expertise"
          subtitle="Technologies and tools I use to bring ideas to life"
          gradient={false}
          centered={true}
        />

        <div className="mt-16 space-y-16">
          {skillCategories.map((category, categoryIndex) => (
            <motion.div
              key={category.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: categoryIndex * 0.1 }}
            >
              {/* Category Title */}
              <div className="mb-8">
                <h3 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-dark-50 mb-2">
                  {category.title}
                </h3>
                <motion.div
                  className="h-1 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-full"
                  initial={{ width: 0 }}
                  whileInView={{ width: '80px' }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: categoryIndex * 0.1 + 0.2 }}
                />
              </div>

              {/* Skills Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-6">
                {category.skills.map((skill, skillIndex) => (
                  <SkillBadge
                    key={skill.name}
                    name={skill.name}
                    icon={skill.icon}
                    proficiency={skill.proficiency}
                    delay={categoryIndex * 0.1 + skillIndex * 0.05}
                  />
                ))}
              </div>
            </motion.div>
          ))}
        </div>

        {/* Additional Skills Note */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-16 text-center"
        >
          <div className="relative inline-block">
            <div className="absolute -inset-1 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-xl opacity-20 blur" />
            <div className="relative bg-white/60 dark:bg-dark-800/60 backdrop-blur-sm rounded-xl px-8 py-4 border border-gray-200 dark:border-dark-700">
              <p className="text-gray-600 dark:text-dark-300">
                Constantly pushing creative boundaries while maintaining a practical approach
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};
